import React, { ReactElement } from 'react';

import { Box } from '@chakra-ui/react';
import GradientCard from 'components/RabbitOKRCreationPanel/components/GradientCard';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import MeshMavenLoading from 'screens/common/MLCommentTextGenerationCompV1/MeshMavenLoading';

import { CheckCircle } from 'assets/assetsV1/iconComponents';

import { StringModifier, modifyString } from 'constants/copyHelpers';

import { TextBodyMedium } from 'uiToolkitV1/Typography';

const AnalysisItemHeaderMap = {
  aspirational: { value: 'Aspirational & Achievable', order: 1 },
  measurability: { value: 'Actionable & measurable', order: 1 },
  objectiveAlignment: { value: 'Business aligned & relevant', order: 2 },
  concise: { value: 'Clear & specific', order: 3 },
};

const AnalysisItem = ({ header, description }) => (
  <Box display="flex" flexDirection="column" alignItems="flex-start" gap="2px" alignSelf="stretch">
    <Box display="flex" padding="2px" alignItems="flex-start" gap="8px" alignSelf="stretch">
      <Box display="flex" width="24px" height="24px" justifyContent="center" alignItems="center">
        <CheckCircle boxSize="24px" color="#2F8133" />
      </Box>
      <Box display="flex" alignItems="flex-start" gap="10px" flex="1 0 0">
        <TextBodyMedium>
          <TextBodyMedium as="span" fontWeight="600">
            {header}
          </TextBodyMedium>
          : {description}
        </TextBodyMedium>
      </Box>
    </Box>
  </Box>
);

export default function GoalMeshMavenAnalysis({ isObjective, suggestedOKRDetails, isLoading, ...rest }): ReactElement {
  const { objectivesName, keyResultsName } = useOrganisationSettings();
  const { explanation = {} } = suggestedOKRDetails || {};
  const analysisItemData = Object.entries(explanation || {})
    .filter(
      ([header, description]) =>
        description && ((isObjective && header !== 'measurability') || (!isObjective && header !== 'aspirational'))
    )
    .map(([header, description]) => ({
      header: AnalysisItemHeaderMap[header]?.value,
      order: AnalysisItemHeaderMap[header]?.order,
      description,
    }))
    .sort((a, b) => a.order - b.order);

  return (
    <Box width="100%" {...rest}>
      <GradientCard title="Goal Analysis">
        {isLoading ? (
          <MeshMavenLoading loadingText="Analyzing recommendations via Maven" width="100%" />
        ) : (
          <Box display="flex" flexDirection="column" alignItems="flex-start" gap="12px" flex="1 0 0">
            <Box display="flex" flexDirection="column" alignItems="flex-start" gap="12px" alignSelf="stretch">
              <TextBodyMedium>
                Maven analyzed your{' '}
                {modifyString(isObjective ? objectivesName : keyResultsName, StringModifier.TITLE_CASE)} and modified it
                to make it
              </TextBodyMedium>
            </Box>
            {analysisItemData.map(({ header, description }) => (
              <AnalysisItem header={header} description={description} />
            ))}
          </Box>
        )}
      </GradientCard>
    </Box>
  );
}
